import { Form, Grid, Label, Popup } from '@mairistem/composants';
import type { Tiers } from '@mairistem/comptabilite';
import { Combobox, Date } from '@mairistem/finances-utils';
import { isNil } from 'lodash';
import React from 'react';
import asapDemat from '../../../../constants/tiers/asapDematerialise';

interface TiersFichePanelParametresGestionProps {
	identifiant: number | string;
	debiteurOuFournisseur: number;
	generique: boolean;
	asapDematerialise: string;
	dateValidite: string;
	delaiPaiement: number;
	engagementObligatoire: boolean;
	onChange: (changes: Partial<Tiers>) => void;
}

const TiersFichePanelParametresGestion = ({
	identifiant,
	debiteurOuFournisseur,
	generique = false,
	asapDematerialise,
	dateValidite,
	delaiPaiement,
	engagementObligatoire,
	onChange,
}: TiersFichePanelParametresGestionProps) => {
	const handleSelectChange = React.useCallback(
		(_e, data) => {
			const { value, name } = data || {};

			if (onChange) {
				onChange({
					identifiant,
					[name]: value,
				});
			}
		},
		[identifiant, onChange],
	);

	const handleCheckboxChange = React.useCallback(
		(_e, data) => {
			const { checked, name } = data || {};

			if (onChange) {
				onChange({
					identifiant,
					[name]: checked,
				});
			}
		},
		[identifiant, onChange],
	);

	const handleDateChange = React.useCallback(
		(value: string, name: string) => {
			onChange?.({
				identifiant,
				[name]: value === '' ? null : value,
			});
		},
		[identifiant, onChange],
	);

	const handleChange = React.useCallback(
		({ target }) => {
			if (onChange) {
				onChange({
					identifiant,
					[target.name]: target.value,
				});
			}
		},
		[identifiant, onChange],
	);

	const renderGenerique = React.useMemo(
		() => (
			<Form.Checkbox
				key="generique"
				name="generique"
				label="Utiliser pour les liquidations nominatives"
				checked={generique}
				onChange={handleCheckboxChange}
			/>
		),
		[generique, handleCheckboxChange],
	);

	const renderAsap = React.useMemo(
		() => (
			<Form.Field
				control={Combobox}
				key="asapDematerialise"
				label="ASAP dématérialisé"
				name="asapDematerialise"
				onChange={handleSelectChange}
				options={asapDemat}
				selection
				style={{ width: '340px' }}
				value={asapDematerialise}
				placeholder="Non dématérialisé par l'ordonnateur"
			/>
		),
		[asapDematerialise, handleSelectChange],
	);

	const renderDateValidite = React.useMemo(
		() => (
			<Popup
				trigger={
					<Form.Field
						control={Date}
						fluid
						key="dateValidite"
						label="Fin de validité (caducité)"
						name="dateValidite"
						onChange={handleDateChange}
						value={dateValidite}
					/>
				}
				content="Après cette date le tiers ne pourra plus être rattaché à une pièce"
				on="hover"
			/>
		),
		[dateValidite, handleDateChange],
	);

	const renderDelaiPaiement = React.useMemo(
		() => (
			<Form.Input
				key="delaiPaiement"
				name="delaiPaiement"
				placeholder=""
				label="DGP Spécifique"
				value={isNil(delaiPaiement) ? '' : delaiPaiement}
				type="number"
				onChange={handleChange}
				labelPosition="left"
				width={14}
				action={<Label basic content="jours" />}
				style={{ width: '150px' }}
			/>
		),
		[delaiPaiement, handleChange],
	);

	const renderEngagementObligatoire = React.useMemo(
		() => (
			<Form.Checkbox
				key="engagementObligatoire"
				name="engagementObligatoire"
				label="Engagement obligatoire"
				checked={engagementObligatoire}
				onChange={handleCheckboxChange}
			/>
		),
		[engagementObligatoire, handleCheckboxChange],
	);

	const displayASAP =
		debiteurOuFournisseur === 1 || debiteurOuFournisseur === 3;

	return (
		<Form autoComplete={false}>
			<Grid padded="vertically">
				<Grid.Row>
					<Grid.Column width={displayASAP ? 3 : 5}>
						{renderDateValidite}
					</Grid.Column>
					<Grid.Column width={displayASAP ? 3 : 5}>
						{renderDelaiPaiement}
					</Grid.Column>
					{displayASAP && <Grid.Column width={5}>{renderAsap}</Grid.Column>}
					<Grid.Column width={displayASAP ? 5 : 6} verticalAlign="middle">
						{renderEngagementObligatoire}
						{renderGenerique}
					</Grid.Column>
				</Grid.Row>
			</Grid>
		</Form>
	);
};

export default TiersFichePanelParametresGestion;
